import type { UserCreationUiPhase, UserFormStepId } from '@/types/userCreation';
import { PROVISIONED_USER_GROUPS } from '@/types/provisionedUser';
import { staticDefaults } from '@/config/static/catalog';

/** Figma section: Add-user-flow-novice */
export const ADD_USER_FIGMA_SECTION = 'Add-user-flow-novice' as const;

export const ADD_USER_FLOW_TITLE = 'Add a user with a credential';

export const USER_CREATION_TRUST_LINE =
  'AI proposes, you approve. No auto-actions on devices, users, or credentials.';

export const ADD_USER_UI_PHASES: { id: UserCreationUiPhase; figmaLabel: string }[] = [
  { id: 'thinking', figmaLabel: 'Thinking...' },
  { id: 'working', figmaLabel: 'Working on it...' },
  { id: 'almost-ready', figmaLabel: 'Almost ready..' },
  { id: 'form', figmaLabel: 'Guided form (details → credential → access group → confirm)' },
  { id: 'saving', figmaLabel: 'Saving' },
  { id: 'success', figmaLabel: 'User created' },
];

export const USER_FORM_STEPS: { id: UserFormStepId; label: string }[] = [
  { id: 'user-details', label: 'User details' },
  { id: 'credential', label: 'Credential' },
  { id: 'access-group', label: 'Access group' },
  { id: 'confirm', label: 'Confirm' },
];

export const USER_PROGRESS_RAIL_STEPS = [
  { id: 'access', label: 'Checking your access' },
  { id: 'system', label: 'Confirming the system' },
  { id: 'validate', label: 'Validating user info' },
  { id: 'preview', label: 'Building your preview' },
] as const;

export const USER_CREATION_TIMING = {
  thinkingMs: 900,
  progressStepMs: [750, 3000, 3000, 800] as const,
  almostReadyMs: 700,
  formRevealMs: 500,
  savingMs: 1200,
} as const;

export const CREDENTIAL_TYPE_OPTIONS = [
  {
    id: 'card' as const,
    title: 'Card only',
    description: 'Assign a card credential for badge-in access at readers.',
  },
  {
    id: 'pin' as const,
    title: 'PIN only',
    description: 'A numeric PIN for keypad authentication at PIN readers.',
  },
  {
    id: 'card-and-pin' as const,
    title: 'Card + PIN',
    description: 'Both a card and PIN for dual-factor authentication.',
  },
];

export const CARD_FORMATS: string[] = [
  '26 bits Standard',
  '26-bit Wiegand (H10301)',
  '34-bit (N10002)',
  '37-bit (H10304)',
  'Corporate 1000 (35-bit)',
  'Custom',
];

/** @deprecated Use staticDefaults.user.accessGroups */
export const DUMMY_ACCESS_GROUPS: string[] = [...staticDefaults.user.accessGroups];

/** @deprecated Use staticDefaults.user.schedules */
export const DUMMY_SCHEDULES: string[] = [...staticDefaults.user.schedules];

/** @deprecated Use staticDefaults.user.defaultDraft */
export const DUMMY_USER_DEFAULT_DRAFT = { ...staticDefaults.user.defaultDraft };

/** @deprecated Use staticDefaults.user.operatorPrefill */
export const DUMMY_OPERATOR_PREFILLED_DRAFT = { ...staticDefaults.user.operatorPrefill };

/** @deprecated Use staticDefaults.user.operatorAiFilledFields */
export const OPERATOR_AI_FILLED_FIELDS = [...staticDefaults.user.operatorAiFilledFields];

/** Form select options — same values as {@link PROVISIONED_USER_GROUPS}. */
export const USER_GROUPS: string[] = [...PROVISIONED_USER_GROUPS];

/** @deprecated Use staticDefaults.user.success */
export const DUMMY_USER_SUCCESS = { ...staticDefaults.user.success };

/** @deprecated Use staticDefaults.user.operatorSuccess */
export const DUMMY_OPERATOR_SUCCESS = { ...staticDefaults.user.operatorSuccess };

/** @deprecated Use staticDefaults.user.explainability */
export const DUMMY_USER_EXPLAINABILITY = staticDefaults.user.explainability;

/** @deprecated Use staticDefaults.user.assistantHint */
export const DUMMY_USER_ASSISTANT_HINT = staticDefaults.user.assistantHint;
